/**
 * WhatsApp helpers + reusable Share button.
 *
 * Uses the backend share endpoints which:
 *  - Build a localized message including line items, totals, public link.
 *  - Return `wa_url` ready to be opened.
 *
 * For the free "click-to-chat" with a customer/supplier we use the lightweight
 * `/whatsapp/chat/:phone` endpoint that doesn't require a paid tier.
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle, Loader2, Lock } from "lucide-react";
import { api } from "@/App";
import { toast } from "sonner";
import { useSubscription, tierLabel } from "@/lib/subscription";
import { useNavigate } from "react-router-dom";

const SHARE_ENDPOINTS = {
  invoice: (id) => `/whatsapp/share/invoice/${id}`,
  order: (id) => `/whatsapp/share/order/${id}`,
  purchase_order: (id) => `/whatsapp/share/purchase-order/${id}`,
  product: (id) => `/whatsapp/share/product/${id}`,
  statement: (id) => `/whatsapp/share/statement/${id}`,
  payment_reminder: (id) => `/whatsapp/payment-reminder/${id}`,
};

const FEATURE_BY_TYPE = {
  invoice: "whatsapp_share",
  order: "whatsapp_share",
  purchase_order: "whatsapp_share",
  product: "whatsapp_advanced",
  statement: "whatsapp_advanced",
  payment_reminder: "whatsapp_advanced",
};

export async function openWhatsAppChat(phone) {
  if (!phone) {
    toast.error("Este contacto no tiene teléfono");
    return;
  }
  try {
    const { data } = await api.get(`/whatsapp/chat/${encodeURIComponent(phone)}`);
    window.open(data.wa_url, "_blank");
  } catch {
    toast.error("No se pudo abrir WhatsApp");
  }
}

/**
 * Renders a green WhatsApp button. If the tenant lacks the required feature,
 * shows a locked button that redirects to the paywall.
 *
 * Props: type, id, label?, variant?, size?
 */
export function WhatsAppShareButton({ type, id, label = "WhatsApp", variant = "outline", size = "sm", testid }) {
  const { hasFeature, tier } = useSubscription();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const feature = FEATURE_BY_TYPE[type] || "whatsapp_share";
  const ok = hasFeature(feature);

  const handle = async () => {
    if (!ok) {
      navigate(`/upgrade?reason=${feature}&from=${tier}`);
      return;
    }
    setLoading(true);
    try {
      const url = SHARE_ENDPOINTS[type]?.(id);
      if (!url) throw new Error("Tipo no soportado");
      const { data } = await api.get(url);
      window.open(data.wa_url, "_blank");
      if (data.public_url) {
        try { await navigator.clipboard.writeText(data.public_url); } catch { /* ignore */ }
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "plan_required") {
        navigate(`/upgrade?reason=${detail.feature}&from=${detail.current_tier}`);
      } else {
        toast.error(detail?.message || detail || "No se pudo generar el enlace de WhatsApp");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      onClick={handle}
      disabled={loading}
      data-testid={testid || `wa-share-${type}-${id}`}
      className={ok ? "border-green-200 text-green-700 hover:bg-green-50" : "border-amber-200 text-amber-700 hover:bg-amber-50"}
    >
      {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : ok ? <MessageCircle className="h-4 w-4 mr-1" /> : <Lock className="h-4 w-4 mr-1" />}
      {ok ? label : `${label} · ${tierLabel("premium")}+`}
    </Button>
  );
}

/** Compact icon-only version for tables. */
export function WhatsAppChatIconButton({ phone, testid }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-green-600 hover:bg-green-50"
      onClick={(e) => { e.stopPropagation(); openWhatsAppChat(phone); }}
      title="Chatear por WhatsApp"
      aria-label="Chatear por WhatsApp"
      data-testid={testid}
    >
      <MessageCircle className="h-4 w-4" />
    </Button>
  );
}
